/**
 * useCashfree.js
 * Loads the official Cashfree JS SDK from CDN and returns an instance.
 * Secret key is NEVER here - only paymentSessionId (returned by backend).
 *
 * The instance is cached PER ENVIRONMENT (sandbox / production). Before, the first
 * environment used was cached forever, so a wrong first value broke every checkout.
 *
 * Usage:
 *   const cf = await loadCashfree('PRODUCTION');   // or 'SANDBOX'
 *   cf.checkout({ paymentSessionId, redirectTarget: '_modal' });
 */

const CASHFREE_SDK_URL = 'https://sdk.cashfree.com/js/v3/cashfree.js';

let _scriptPromise = null;
const _instances = {};

function loadScript() {
  if (window.Cashfree) return Promise.resolve();
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src   = CASHFREE_SDK_URL;
    script.async = true;
    script.onload = () => {
      if (!window.Cashfree) {
        _scriptPromise = null;
        reject(new Error('Cashfree SDK loaded but window.Cashfree is undefined'));
        return;
      }
      resolve();
    };
    script.onerror = () => {
      _scriptPromise = null;
      reject(new Error('Failed to load Cashfree SDK. Check internet connection.'));
    };
    document.head.appendChild(script);
  });
  return _scriptPromise;
}

export async function loadCashfree(environment = 'SANDBOX') {
  const mode = String(environment).toUpperCase() === 'PRODUCTION' ? 'production' : 'sandbox';
  await loadScript();
  if (!_instances[mode]) _instances[mode] = window.Cashfree({ mode });
  return _instances[mode];
}
